import React, { useMemo, useState } from 'react';
import { styled } from 'react-free-style';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { colors } from '@united-talent-agency/components';
import { SortableColumnHeader, Checkbox } from '@united-talent-agency/components';
import { CALL_LIST } from '../../support/cypressTags';
import BulkDeleteCommunication from '../../components/BulkDeleteCommunication';
import DeleteCallModal from '../../components/DeleteCallModal';
import BulkUpdateStatus from '../../components/BulkUpdateStatus';
import {
  useDeleteCallRecordMutation,
  CallRecordsDocument,
  CallRecordsDuplicateCheckDocument,
} from '../../generated/graphql';
import CallsStatusGroup from '../../components/CallsStatusGroup';

const Component = ({
  user,
  desk,
  styles,
  search,
  callsToDo,
  selectAll,
  onEditDone,
  onLoadMore,
  fetchCounts,
  totalCounts,
  deskStatuses,
  selectExcept,
  setSelectAll,
  editCallTodo,
  setSelectExcept,
  statusOfLoadingMore,
  hideFutureCalls = false,
}) => {
  const [deleteCallRecordMutation] = useDeleteCallRecordMutation({
    refetchQueries: [CallRecordsDocument, CallRecordsDuplicateCheckDocument],
  });
  const { companyName: showCompanyName = false } = useFlags();

  // States
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  /**
   * Group calls by status
   */
  const callsByStatus = useMemo(
    () =>
      callsToDo.reduce((agg, callTodo) => {
        if (!agg[callTodo.status]) {
          agg[callTodo.status] = [];
        }
        agg[callTodo.status].push(callTodo);
        return agg;
      }, {}),
    [callsToDo]
  );

  /**
   * Sorts the list of calls by existing statuses in "statuses" and places the unfound statuses in "statuses" at the end of the list
   */
  const list = useMemo(
    () =>
      Object.entries(callsByStatus || {}).sort((a, b) => {
        const i1 = deskStatuses.findIndex((status) => status.status === a[0]);
        const i2 = deskStatuses.findIndex((status) => status.status === b[0]);

        return (i1 === -1 ? deskStatuses.length : i1) - (i2 === -1 ? deskStatuses.length : i2);
      }),
    [callsByStatus, deskStatuses]
  );

  const hideBulkEdits = desk?.settings?.hideBulkEdits;

  if (!deskStatuses) {
    return <div />;
  }

  return (
    <div className={styles.container}>
      {!hideBulkEdits && (
        <div className={styles.bulkActionContainer}>
          <BulkUpdateStatus
            user={user}
            desk={desk}
            selectAll={selectAll}
            selectExcept={selectExcept}
            statuses={deskStatuses}
            filterParams={search}
            callsToDo={callsToDo}
            hideFutureCalls={hideFutureCalls}
            onUpdateApplied={(updatedCall, originalCall, bulkOperation) => {
              setSelectAll(false);
              setSelectExcept(new Set());
              onEditDone && onEditDone(updatedCall, originalCall, bulkOperation);
            }}
          />
          <span className={styles.pipe} />
          <BulkDeleteCommunication
            user={user}
            desk={desk}
            selectAll={selectAll}
            selectExcept={selectExcept}
            filterParams={search}
            callsToDo={callsToDo}
            hideFutureCalls={hideFutureCalls}
            onUpdateApplied={(updatedCall, originalCall, bulkOperation) => {
              setSelectAll(false);
              setSelectExcept(new Set());
              onEditDone && onEditDone(updatedCall, originalCall, bulkOperation);
            }}
          />
        </div>
      )}
      <table
        id="callTable"
        className={styles.table}
        data-cy={CALL_LIST.CALL_LIST}
        data-hide-company={!showCompanyName}
        data-hide-bulk-edit={hideBulkEdits}
      >
        <thead>
          <tr className={styles.tr}>
            {!hideBulkEdits && (
              <th name="checkbox" className={styles.checkboxColumn}>
                <Checkbox
                  checked={selectAll}
                  indeterminate={selectExcept?.size}
                  clearedBackground
                  onChange={(e) => {
                    const { checked } = e.currentTarget;
                    setSelectAll(checked);
                    setSelectExcept(new Set());
                  }}
                />
              </th>
            )}
            <th name="status" style={{ textAlign: 'center' }} className={styles.statusColumn}>
              <SortableColumnHeader text="Status" lightWeight />
            </th>
            <th name="starred" className={styles.starredColumn} />
            <th name="name" className={styles.nameColumn}>
              <SortableColumnHeader text="Name" lightWeight />
            </th>
            <th name="company" className={styles.companyColumn}>
              <SortableColumnHeader text="Company" lightWeight />
            </th>
            <th name="contact" style={{ paddingLeft: 7 }} className={styles.contactColumn}>
              <SortableColumnHeader text="Contact" lightWeight />
            </th>
            <th name="date" className={styles.dateColumn}>
              <SortableColumnHeader text="Date" lightWeight />
            </th>
            <th name="notes" className={styles.notesColumn} style={{ paddingLeft: 8 }}>
              <SortableColumnHeader text="Notes" lightWeight />
            </th>
            <th
              name="actions"
              style={{ textAlign: 'end', paddingLeft: 12 }}
              className={styles.actionsColumn}
            >
              <SortableColumnHeader text="Actions" lightWeight />
            </th>
          </tr>
        </thead>
        {list?.map(([status, calls]) => {
          const hasMore = fetchCounts[status] < totalCounts[status];

          return (
            <CallsStatusGroup
              user={user}
              key={status}
              desk={desk}
              calls={calls}
              status={status}
              hasMore={hasMore}
              statuses={deskStatuses}
              selectAll={selectAll}
              onLoadMore={onLoadMore}
              onEditDone={onEditDone}
              fetchCounts={fetchCounts}
              totalCounts={totalCounts}
              selectExcept={selectExcept}
              editCallTodo={editCallTodo}
              setSelectExcept={setSelectExcept}
              statusOfLoadingMore={statusOfLoadingMore}
              setToDelete={setToDelete}
              setShowDeleteDialog={setShowDeleteDialog}
            />
          );
        })}
      </table>
      <DeleteCallModal
        isOpen={showDeleteDialog}
        onConfirm={() => {
          deleteCallRecordMutation({
            variables: {
              id: toDelete?._id,
            },
          }).then(() => {
            // Call onEditDone with null (deleted) and original call data
            onEditDone && onEditDone(null, toDelete);
          });
          setToDelete(null);
          setShowDeleteDialog(false);
        }}
        onCancel={() => {
          setShowDeleteDialog(false);
        }}
      />
    </div>
  );
};

const withStyles = styled({
  container: {
    containerName: 'call-list-container',
    containerType: 'inline-size',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 10,
    tableLayout: 'fixed',
    ['&[data-hide-company="true"]']: {
      ['[name="checkbox"]']: {
        width: '6%',
      },
      ['[name="status"]']: {
        width: '8%',
      },
      ['[name="starred"]']: {
        width: '7%',
      },
      ['[name="name"]']: {
        width: '12%',
      },
      ['[name="company"]']: {
        display: 'none',
      },
      ['[name="contact"]']: {
        width: '17%',
      },
      ['[name="date"]']: {
        width: '14%',
      },
      ['[name="notes"]']: {
        width: '28%',
      },
      ['[name="actions"]']: {
        width: '8%',
      },
      ['&[data-hide-bulk-edit="true"]']: {
        ['[name="actions"]']: {
          width: '14%',
        },
      },
    },
    ['&[data-hide-company="false"][data-hide-bulk-edit="true"] [name="actions"]']: {
      width: '8%',
    },
  },
  tr: {
    '> th': {
      color: colors.darkGrey,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingLeft: 2.5,
      paddingRight: 2.5,
      paddingBottom: 3,
    },
  },
  bulkActionContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginRight: 15,
    marginLeft: 15,
  },
  divider: {
    height: 20,
  },
  pane: {
    background: colors.contentBackground,
  },
  pipe: {
    display: 'inline-block',
    width: 1,
    height: 20,
    backgroundColor: colors.disabledGrey,
    margin: '0 10px',
  },
  bulkStatusUpdateItem: {
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 5,
    paddingBottom: 5,
    '&:hover': { backgroundColor: 'rgba(41, 161, 219, 0.15)' },
  },

  // #region Columns style
  checkboxColumn: {
    width: '3%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '6%',
    },
  },
  statusColumn: {
    width: '5%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '8%',
    },
  },
  starredColumn: {
    width: '2%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '7%',
    },
  },
  nameColumn: {
    width: '11%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '12%',
    },
  },
  companyColumn: {
    width: '25%',
    ['@container call-list-container (max-width: 1150px)']: {
      display: 'none',
    },
  },
  contactColumn: {
    width: '14%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '17%',
    },
  },
  dateColumn: {
    width: '10%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '14%',
    },
  },
  notesColumn: {
    width: '25%',
    ['@container call-list-container (max-width: 1150px)']: {
      width: '28%',
    },
  },
  actionsColumn: {
    width: '8%',
  },
  // #endregion Columns style
});

const CallList = withStyles(Component);

export default CallList;
